import {
  type GuidedPlanningQuote,
  isTraditionalLTCQuote,
} from '@/features/guided-financial-planning/hooks/use-quotes-display';

export interface PremiumBounds {
  min: number;
  max: number;
  defaultValue: number;
  step: number;
}

export const DEFAULT_PREMIUM_BOUNDS: PremiumBounds = {
  min: 50000,
  max: 300000,
  defaultValue: 100000,
  step: 1000,
};

export function snapPremiumToStep(value: number, min: number, max: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : min;
  }
  if (!Number.isFinite(step) || step <= 0) {
    return Math.max(min, Math.min(max, value));
  }
  const clamped = Math.max(min, Math.min(max, value));
  const snapped = Math.round(clamped / step) * step;
  return Math.max(min, Math.min(max, snapped));
}

/**
 * Union of carrier investmentMin/investmentMax across adjustable quotes, or null if not derivable.
 */
export function tryPremiumBoundsFromQuotes(
  quotes: GuidedPlanningQuote[] | undefined,
): PremiumBounds | null {
  if (!quotes || quotes.length === 0) {
    return null;
  }

  const adjustablePolicyQuotes = quotes.filter(
    (quote): quote is PolicyQuote => !isTraditionalLTCQuote(quote),
  );

  const investmentMins = adjustablePolicyQuotes
    .map(quote => quote.investmentMin)
    .filter((value): value is number => typeof value === 'number' && value > 0);
  const investmentMaxs = adjustablePolicyQuotes
    .map(quote => quote.investmentMax)
    .filter((value): value is number => typeof value === 'number' && value > 0);

  if (investmentMins.length === 0 || investmentMaxs.length === 0) {
    return null;
  }

  const leastRestrictiveMinimum = Math.min(...investmentMins);
  const leastRestrictiveMaximum = Math.max(...investmentMaxs);
  const rawMax = Math.max(leastRestrictiveMinimum, leastRestrictiveMaximum);
  const step = rawMax <= 1000 ? 10 : rawMax <= 10000 ? 100 : 1000;
  const min = Math.ceil(Math.min(leastRestrictiveMinimum, leastRestrictiveMaximum) / step) * step;
  const max = Math.floor(Math.max(leastRestrictiveMinimum, leastRestrictiveMaximum) / step) * step;

  if (max <= min) {
    return null;
  }

  const firstQuotePremium = adjustablePolicyQuotes[0]?.premiumAmount;
  const defaultValueRaw =
    typeof firstQuotePremium === 'number' && firstQuotePremium > 0
      ? Math.max(min, Math.min(max, firstQuotePremium))
      : Math.floor((min + max) / 2);
  const defaultValue = snapPremiumToStep(defaultValueRaw, min, max, step);

  if (min === max) {
    return {
      min,
      max: min + step,
      defaultValue,
      step,
    };
  }

  return {
    min,
    max,
    defaultValue,
    step,
  };
}

/**
 * Derives premium bounds from traditional LTC quotes using their actual premium amounts.
 * Used for tabs that contain only traditional quotes (Monthly, Annual).
 */
export function premiumBoundsFromTraditionalQuotes(
  quotes: GuidedPlanningQuote[],
): PremiumBounds | null {
  const traditionalQuotes = quotes.filter(isTraditionalLTCQuote);
  if (traditionalQuotes.length === 0) return null;

  const premiums = traditionalQuotes
    .map(q => q.primaryModalPremium)
    .filter((v): v is number => typeof v === 'number' && v > 0);

  if (premiums.length === 0) return null;

  const rawMin = Math.min(...premiums);
  const rawMax = Math.max(...premiums);

  if (rawMin === rawMax) return null;

  const step = rawMax <= 1000 ? 10 : rawMax <= 10000 ? 100 : 1000;
  const min = Math.floor(rawMin / step) * step;
  const max = Math.ceil(rawMax / step) * step;

  return {
    min,
    max,
    defaultValue: max,
    step,
  };
}

/**
 * Computes premium bounds for a tab's quotes, handling both hybrid (adjustable) and
 * traditional (filterable) quotes. Returns null if the slider would be meaningless.
 */
export function computeTabPremiumBounds(
  quotes: GuidedPlanningQuote[],
): PremiumBounds | null {
  const hybridBounds = tryPremiumBoundsFromQuotes(quotes);
  const traditionalBounds = premiumBoundsFromTraditionalQuotes(quotes);

  if (hybridBounds && traditionalBounds) {
    const min = Math.min(hybridBounds.min, traditionalBounds.min);
    const max = Math.max(hybridBounds.max, traditionalBounds.max);
    const step = Math.min(hybridBounds.step, traditionalBounds.step);
    return { min, max, step, defaultValue: max };
  }

  return hybridBounds ?? traditionalBounds;
}

